/**
 * BackButton — large, terminal-themed back navigation button.
 *
 * Replaces the bare-icon `<ArrowLeft />` patterns scattered across page
 * headers. Provides a 48×48px tap target (Apple HIG minimum) with a visible
 * border so it reads as tappable on mobile.
 *
 * Usage:
 *   <BackButton to="/customer" />
 *   <BackButton to="/dispatch" label="BACK TO DISPATCH" />
 *   <BackButton onClick={handleCustomBack} />
 */
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

export function BackButton({ to, onClick, label, className = '', testid = 'back-btn' }) {
  const navigate = useNavigate();
  const { isModern } = useTheme();
  const baseClasses = isModern ? 'am-back-btn' :
    'inline-flex items-center justify-center gap-2 ' +
    'min-w-[48px] min-h-[48px] px-3 ' +
    'border border-[#2A0F3D] text-[#A3C7A8] ' +
    'hover:border-[#39FF14] hover:text-[#39FF14] hover:bg-[rgba(57,255,20,0.04)] ' +
    'active:bg-[rgba(57,255,20,0.1)] ' +
    'transition-colors duration-150 ' +
    'font-mono text-sm';

  const content = (
    <>
      <ArrowLeft className={isModern ? 'w-5 h-5' : 'w-6 h-6'} strokeWidth={2.25} />
      {label && <span>{label}</span>}
    </>
  );

  if (to) {
    return (
      <Link to={to} className={`${baseClasses} ${className}`} data-testid={testid}>
        {content}
      </Link>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick || (() => navigate(-1))}
      className={`${baseClasses} ${className}`}
      data-testid={testid}
      aria-label="Go back"
    >
      {content}
    </button>
  );
}
